import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Settings } from '@mui/icons-material';
import {
  Brush,
  CartesianGrid,
  ErrorBar,
  Label,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import PlotSettings from '../PlotSettings';
import MetRateTooltip from './MetRateTooltip';
import PlotStats from '../PlotStats';
import { formatTimestampHHMMSS } from '../../../helpers/datetime';

const ev1Stroke = '#215880';
const ev2Stroke = '#64A6d7';
const measure = 'mr';
const unit = 'BTU/HR';

const CHART_HEIGHT = 400;
const CHART_WIDTH = 660;
const COMPACT_HEIGHT = 280;
const BRUSH_HEIGHT = 40;
const COMPACT_BRUSH_HEIGHT = 24;

export default function MetRate({
  data = [],
  maxBtuHR = 400,
  minBtuHR = 1700,
  hideSettings = false,
  compact = false,
  suitPressure = null,
}) {
  const [yMin, setYMin] = useState(400);
  const [yMax, setYMax] = useState(1700);
  const [btuTickCount, setBTUTickCount] = useState(15);

  const [chartWindowMinNum, setChartWindowMinNum] = useState(0);

  const [start, setStartIndex] = useState(0);
  const [end, setEndIndex] = useState(0);

  const [settingsOpen, setSettingsOpen] = useState(false);

  const [ev1Active, setEV1Active] = useState(true);
  const [ev2Active, setEV2Active] = useState(true);

  const closeSettings = () => setSettingsOpen(false);

  const handleBrushUpdate = ({ startIndex, endIndex }) => {
    setStartIndex(startIndex);
    setEndIndex(((data.length - 1) - endIndex <= 1) ? null : endIndex);
  };

  const togglePlotSettings = () => setSettingsOpen(!settingsOpen);

  const last = data.at(-1);
  const hasData = data.length > 0;
  const displayTimestamp = last?.formattedTimestamp
    || (last?.timestamp != null ? formatTimestampHHMMSS(last.timestamp) : undefined);

  const suitPressureByEv = (suitPressure != null && typeof suitPressure === 'object')
    ? suitPressure
    : {};
  // Back-compat: a bare scalar/string means a single (unattributed) pressure.
  const legacySuitPressure = (suitPressure != null && typeof suitPressure !== 'object' && suitPressure !== '')
    ? suitPressure
    : null;
  const ev1Pressure = suitPressureByEv.EV1 ?? null;
  const ev2Pressure = suitPressureByEv.EV2 ?? null;
  const hasPerEvPressure = ev1Pressure != null || ev2Pressure != null;

  return (
    <div className={`met-rate plot${compact ? ' compact' : ''}`}>
      <div className="header">
        <span>Met Rate</span>
        {!hideSettings && (
        <div className="settings-controller">
          <Settings data-testid="met-rate-plot-settings" onClick={togglePlotSettings} />
          {settingsOpen
            ? (
              <PlotSettings
                setYMin={setYMin}
                startYMin={yMin}
                setYMax={setYMax}
                startYMax={yMax}
                setTickCount={setBTUTickCount}
                startTickCount={btuTickCount}
                setChartWindow={setChartWindowMinNum}
                startChartWindow={chartWindowMinNum}
              />
            ) : null}
        </div>
        )}
      </div>
      <div className="stats flex-row" onClick={closeSettings} role="presentation">
        <PlotStats
          color={ev1Stroke}
          data={last?.EV1mr}
          avg={last?.EV1avgMr}
          isActive={ev1Active}
          setActive={() => setEV1Active(!ev1Active)}
          timestamp={displayTimestamp}
          hasData={hasData}
          showAvg
          ev="EV1"
          unit={unit}
        />
        <PlotStats
          color={ev2Stroke}
          data={last?.EV2mr}
          avg={last?.EV2avgMr}
          isActive={ev2Active}
          setActive={() => setEV2Active(!ev2Active)}
          timestamp={displayTimestamp}
          hasData={hasData}
          showAvg
          ev="EV2"
          unit={unit}
        />
      </div>
      <span>{unit}</span>
      {hasPerEvPressure && (
        <span className="suit-pressure-label">
          {'Suit Pressure: '}
          {ev1Pressure != null && (
            <span className="suit-pressure-ev" style={{ color: ev1Stroke }}>
              {`EV1 ${ev1Pressure} psid`}
            </span>
          )}
          {ev1Pressure != null && ev2Pressure != null && ' · '}
          {ev2Pressure != null && (
            <span className="suit-pressure-ev" style={{ color: ev2Stroke }}>
              {`EV2 ${ev2Pressure} psid`}
            </span>
          )}
        </span>
      )}
      {!hasPerEvPressure && legacySuitPressure != null && (
        <span className="suit-pressure-label">
          {`Suit Pressure: ${legacySuitPressure} psid`}
        </span>
      )}
      <div className="met-rate-chart-container" onClick={closeSettings} role="presentation">
        {compact ? (
          <ResponsiveContainer width="100%" height={COMPACT_HEIGHT}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timestamp" interval="preserveStartEnd" />
              <YAxis
                yAxisId={measure}
                domain={[Math.max((yMin == null ? minBtuHR : yMin), 0), yMax || maxBtuHR]}
                interval={0}
                tickCount={btuTickCount}
                tickFormatter={Math.round}
                allowDataOverflow
                type="number"
              >
                <Label position="top" textAnchor="start" value={unit} />
              </YAxis>
              <Tooltip content={<MetRateTooltip />} />
              {ev1Active
                ? (
                  <Line
                    animationDuration={50}
                    animationEasing="linear"
                    dataKey={`EV1${measure}`}
                    dot={false}
                    isAnimationActive
                    key={`EV1${measure}`}
                    stroke={ev1Stroke}
                    strokeWidth={2}
                    type="monotone"
                    yAxisId={measure}
                  />
                ) : null}
              {ev1Active
                ? (
                  <ErrorBar
                    direction="y"
                    width={4}
                    strokeWidth={2}
                    opacity={0.5}
                    fill="green"
                    dataKey="EV1covarMr"
                    key="EV1covarMr"
                    stroke={ev1Stroke}
                  />
                ) : null}
              {ev2Active
                ? (
                  <Line
                    animationDuration={50}
                    animationEasing="linear"
                    dataKey={`EV2${measure}`}
                    dot={false}
                    isAnimationActive
                    key={`EV2${measure}`}
                    stroke={ev2Stroke}
                    strokeWidth={2}
                    type="monotone"
                    yAxisId={measure}
                  />
                ) : null}
              {ev2Active
                ? (
                  <ErrorBar
                    direction="y"
                    width={4}
                    strokeWidth={2}
                    opacity={0.5}
                    fill="green"
                    dataKey="EV2covarMr"
                    key="EV2covarMr"
                    stroke={ev2Stroke}
                  />
                ) : null}
              {(data && data.length)
                ? (
                  <Brush
                    data={data}
                    dataKey="name"
                    height={compact ? COMPACT_BRUSH_HEIGHT : BRUSH_HEIGHT}
                    padding={{
                      left: 0,
                      right: 10,
                    }}
                    tick
                    startIndex={start || (chartWindowMinNum ? Math.max(data.length - 60 * chartWindowMinNum, 0) : 0)}
                    endIndex={end || (data.length - 1)}
                    onChange={handleBrushUpdate}
                    travellerWidth={6}
                  >
                    <LineChart data={data}>
                      <Line
                        animationDuration={50}
                        animationEasing="linear"
                        dataKey={`EV1${measure}`}
                        dot={false}
                        isAnimationActive
                        key={`EV1${measure}`}
                        stroke={ev1Stroke}
                        type="monotone"
                      />
                      <Line
                        animationDuration={50}
                        animationEasing="linear"
                        dataKey={`EV1${measure}`}
                        dot={false}
                        isAnimationActive
                        key={`EV1${measure}`}
                        stroke={ev2Stroke}
                        type="monotone"
                      />
                    </LineChart>
                  </Brush>
                ) : null}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <LineChart data={data} height={CHART_HEIGHT} width={CHART_WIDTH}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="timestamp" interval="preserveStartEnd" />
            <YAxis
              yAxisId={measure}
              domain={[Math.max((yMin == null ? minBtuHR : yMin), 0), yMax || maxBtuHR]}
              interval={0}
              tickCount={btuTickCount}
              tickFormatter={Math.round}
              allowDataOverflow
              type="number"
            >
              <Label position="top" textAnchor="start" value={unit} />
            </YAxis>
            <Tooltip content={<MetRateTooltip />} />
            {ev1Active
              ? (
                <Line
                  animationDuration={50}
                  animationEasing="linear"
                  dataKey={`EV1${measure}`}
                  dot={false}
                  isAnimationActive
                  key={`EV1${measure}`}
                  stroke={ev1Stroke}
                  strokeWidth={2}
                  type="monotone"
                  yAxisId={measure}
                />
              ) : null}
            {ev1Active
              ? (
                <ErrorBar
                  direction="y"
                  width={4}
                  strokeWidth={2}
                  opacity={0.5}
                  fill="green"
                  dataKey="EV1covarMr"
                  key="EV1covarMr"
                  stroke={ev1Stroke}
                />
              ) : null}
            {ev2Active
              ? (
                <Line
                  animationDuration={50}
                  animationEasing="linear"
                  dataKey={`EV2${measure}`}
                  dot={false}
                  isAnimationActive
                  key={`EV2${measure}`}
                  stroke={ev2Stroke}
                  strokeWidth={2}
                  type="monotone"
                  yAxisId={measure}
                />
              ) : null}
            {ev2Active
              ? (
                <ErrorBar
                  direction="y"
                  width={4}
                  strokeWidth={2}
                  opacity={0.5}
                  fill="green"
                  dataKey="EV2covarMr"
                  key="EV2covarMr"
                  stroke={ev2Stroke}
                />
              ) : null}
            {(data && data.length)
              ? (
                <Brush
                  data={data}
                  dataKey="name"
                  height={BRUSH_HEIGHT}
                  padding={{
                    left: 0,
                    right: 10,
                  }}
                  tick
                  startIndex={start || (chartWindowMinNum ? Math.max(data.length - 60 * chartWindowMinNum, 0) : 0)}
                  endIndex={end || (data.length - 1)}
                  onChange={handleBrushUpdate}
                  travellerWidth={6}
                >
                  <LineChart data={data}>
                    <Line
                      animationDuration={50}
                      animationEasing="linear"
                      dataKey={`EV1${measure}`}
                      dot={false}
                      isAnimationActive
                      key={`EV1${measure}`}
                      stroke={ev1Stroke}
                      type="monotone"
                    />
                    <Line
                      animationDuration={50}
                      animationEasing="linear"
                      dataKey={`EV1${measure}`}
                      dot={false}
                      isAnimationActive
                      key={`EV1${measure}`}
                      stroke={ev2Stroke}
                      type="monotone"
                    />
                  </LineChart>
                </Brush>
              ) : null}
          </LineChart>
        )}
      </div>
    </div>
  );
}

MetRate.propTypes = {
  data: PropTypes.any,
  minBtuHR: PropTypes.number,
  maxBtuHR: PropTypes.number,
  hideSettings: PropTypes.bool,
  compact: PropTypes.bool,
  suitPressure: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
};
